import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Zap,
  PanelTop,
  Home as HomeIcon,
  Wrench,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import { SITE, IMAGES, SERVICES } from "@/lib/site";

const ICONS = { PanelTop, Home: HomeIcon, Zap, Wrench };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A1128] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/85 to-[#0A1128]/40" aria-hidden />
        <div className="absolute inset-0 noise-overlay opacity-[0.06]" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-24 md:pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-6">
              <ShieldCheck className="w-3.5 h-3.5" />
              Erkend & Gecertificeerd
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tighter">
              Vakkundig{" "}
              <span className="text-[#FF6B00]">elektrawerk</span>.
              <br />
              Netjes, veilig, op maat.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              Van groepenkast tot laadpaal. Vakwerk in Vlaardingen, Schiedam, Rotterdam en
              omstreken — met strak kabelmanagement en 100% veiligheidscontrole.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={`https://wa.me/${SITE.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#20BD5A] transition-colors shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp direct
              </a>
              <a
                href={`tel:${SITE.phoneIntl}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF6B00] text-white font-bold hover:bg-[#E66000] transition-colors shadow-lg hover:shadow-xl"
              >
                <Phone className="w-5 h-5" />
                Bel {SITE.phoneDisplay}
              </a>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-white/30 text-white font-bold hover:bg-white hover:text-[#0A1128] transition-colors"
              >
                Offerte aanvragen
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              {[
                "Erkend installateur",
                "Nette afwerking",
                "Snelle service",
                "Werkgebied 25 km",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#FF6B00]" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SPECIALTIES / BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">
              Onze specialiteiten
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A1128] max-w-2xl">
              Vier vakgebieden, één betrouwbare installateur.
            </h2>
          </div>
          <Link
            to="/diensten"
            className="inline-flex items-center gap-2 text-[#0A1128] font-bold hover:text-[#FF6B00] transition-colors"
            data-testid="home-all-services-link"
          >
            Alle diensten <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const spanClass =
              i === 0 ? "md:col-span-4" : i === 1 ? "md:col-span-2" : i === 2 ? "md:col-span-2" : "md:col-span-4";
            return (
              <Link
                key={s.slug}
                to={`/diensten#${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-2xl transition-transform ${spanClass}`}
              >
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#0A1128] text-[#FF6B00] group-hover:bg-[#FF6B00] group-hover:text-white transition-colors">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-[#0A1128]">{s.title}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{s.short}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[#FF6B00] font-bold text-sm">
                    Meer weten <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* VALUE PROP STRIPE */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {[
            { n: "5+", l: "Jaar ervaring" },
            { n: "250+", l: "Projecten opgeleverd" },
            { n: "NEN 3140", l: "Gecertificeerd" },
            { n: "VCA Vol", l: "Gecertificeerd" },
            { n: "25 km", l: "Werkgebied" },
            { n: "100%", l: "Erkende installaties" },
          ].map((k) => (
            <div key={k.l} className="text-center md:text-left">
              <div className="font-display text-2xl md:text-3xl lg:text-4xl font-black text-[#0A1128] leading-tight">{k.n}</div>
              <div className="mt-2 text-xs md:text-sm font-semibold text-slate-500 uppercase tracking-wider">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY GRID */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="home-gallery">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">
            Werk in beeld
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A1128] max-w-2xl">
            Netjes weggewerkt. Vakkundig geïnstalleerd.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 grid-rows-2 gap-3 md:gap-4 h-[520px] md:h-[600px]">
          <div className="col-span-2 row-span-2 overflow-hidden rounded-2xl bg-slate-100">
            <img src={IMAGES.serviceEvCharger} alt="Laadpaal met EV" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-slate-100">
            <img src={IMAGES.serviceFuseBox} alt="Groepenkast" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-slate-100">
            <img src={IMAGES.serviceEvChargerDetail} alt="Zaptec laadpaal detail" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-slate-100">
            <img src={IMAGES.serviceSmartHome} alt="Smart home apparatuur" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          <div className="overflow-hidden rounded-2xl bg-slate-100">
            <img src={IMAGES.serviceAllround} alt="Stopcontact installatie" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20 md:pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-[#0A1128] text-white p-10 md:p-16">
          <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-[#FF6B00]/20 blur-3xl" aria-hidden />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-4">
                <MapPin className="w-3.5 h-3.5" /> Actief in {SITE.region}
              </div>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl">
                Plan vandaag nog je vrijblijvende offerte.
              </h3>
              <p className="mt-3 text-slate-300 max-w-xl">
                Bel, WhatsApp of stuur een berichtje. Vaak binnen 1 werkdag antwoord.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3">
              <a href={`tel:${SITE.phoneIntl}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#FF6B00] text-white font-bold hover:bg-[#E66000] transition-colors" data-testid="home-cta-call">
                <Phone className="w-5 h-5" /> Bel nu
              </a>
              <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#20BD5A] transition-colors" data-testid="home-cta-whatsapp">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
